import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Space, Typography, Switch, Tooltip, Popover, List, AutoComplete, Row, Col, Tag } from 'antd';
import { MinusCircleOutlined, PlusOutlined, ArrowRightOutlined, QuestionCircleOutlined, InsertRowBelowOutlined, DeleteOutlined, ClockCircleOutlined } from '@ant-design/icons';
import globalVariableService from '../../../services/GlobalVariableService';

const { Text } = Typography;

// Custom Input component that shows a list of columns on click/focus and inserts selected column at cursor
const FormulaInput = ({ value = '', onChange, options = [] }) => {
  const [open, setOpen] = useState(false);
  const [filterToken, setFilterToken] = useState('');
  const [localValue, setLocalValue] = useState(value); // Local state để tránh race condition
  const inputRef = useRef(null);
  const isComposingRef = useRef(false); // Track IME composition

  // Sync local value khi prop value thay đổi từ bên ngoài (không phải do user input)
  useEffect(() => {
    if (value !== localValue && !inputRef.current?.resizableTextArea?.textArea?.matches(':focus') && !inputRef.current?.input?.matches(':focus')) {
      setLocalValue(value);
    }
  }, [value]);

  // Helper to find the word at cursor
  const getWordAtCursor = (text, position) => {
    const left = text.slice(0, position);
    // Match word characters (letters, numbers, underscore) at the end of the string
    const match = left.match(/[a-zA-Z0-9_]+$/);
    return match ? { word: match[0], start: match.index } : { word: '', start: position };
  };

  const updateFilter = (target) => {
    const pos = target.selectionStart;
    const val = target.value;
    const { word } = getWordAtCursor(val, pos);
    setFilterToken(word);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Cập nhật local state ngay lập tức
    onChange(newValue); // Notify parent
    updateFilter(e.target);
    if (!open) setOpen(true);
  };

  // Handle IME composition (for Vietnamese, Chinese, Japanese input)
  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (e) => {
    isComposingRef.current = false;
    // Trigger change after composition ends
    handleChange(e);
  };

  const handleClick = (e) => {
    updateFilter(e.target);
    // If already open, stop propagation to prevent toggling off
    if (open) {
      e.stopPropagation();
    }
  };

  const handleKeyUp = (e) => {
    updateFilter(e.target);
  };

  const handleInsert = (text) => {
    // Support both Input and TextArea refs
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;
    
    // Auto-quote if column name has spaces or special chars
    const textToInsert = /[^a-zA-Z0-9_]/.test(text) ? `"${text}"` : text;

    if (input) {
      const currentPos = input.selectionStart || 0;
      const { word, start } = getWordAtCursor(localValue, currentPos);
      
      const val = localValue || '';
      // Replace the current word (filter token) with the selected column
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      setLocalValue(newValue); // Update local state
      onChange(newValue); // Notify parent

      // Restore focus and move cursor after inserted text
      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken(''); // Reset filter after insertion
      }, 0);
    } else {
      const newValue = (localValue || '') + textToInsert;
      setLocalValue(newValue);
      onChange(newValue);
    }
    setOpen(false);
  };

  const filteredOptions = options.filter(opt => 
    !filterToken || opt.value.toLowerCase().includes(filterToken.toLowerCase())
  );

  // Get global variables from service
  const globalVariables = globalVariableService.getAllVariables();
  const variableEntries = Object.entries(globalVariables);
  const filteredVariables = variableEntries.filter(([name]) => 
    !filterToken || name.toLowerCase().includes(filterToken.toLowerCase())
  );

  const handleInsertVariable = (varName) => {
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;
    const textToInsert = `{{${varName}}}`;
    if (input) {
      const currentPos = input.selectionStart || 0;
      const { start } = getWordAtCursor(localValue, currentPos);
      const val = localValue || '';
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      setLocalValue(newValue);
      onChange(newValue);
      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken('');
      }, 0);
    } else {
      const newValue = (localValue || '') + textToInsert;
      setLocalValue(newValue);
      onChange(newValue);
    }
    setOpen(false);
  };

  const content = (
    <div style={{ width: 300, maxHeight: 350, overflowY: 'auto' }}>
      {/* Columns Section */}
      <div style={{ padding: '4px 8px', background: '#f5f5f5', borderBottom: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong>Columns</Text> {filterToken && <Text type="secondary" style={{fontSize: 11}}>(filtering by "{filterToken}")</Text>}
      </div>
      <List
        dataSource={filteredOptions}
        style={{ maxHeight: 120, overflowY: 'auto' }}
        renderItem={item => (
          <List.Item
            onClick={() => handleInsert(item.value)}
            style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
            className="formula-list-item"
            onMouseEnter={(e) => e.currentTarget.style.background = '#e6f7ff'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Text code>{item.value}</Text>
          </List.Item>
        )}
        locale={{ emptyText: <Text type="secondary" style={{ fontSize: 11 }}>No matching columns</Text> }}
      />
      
      {/* Global Variables Section */}
      <div style={{ padding: '4px 8px', background: '#f6ffed', borderBottom: '1px solid #b7eb8f', borderTop: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong style={{ color: '#52c41a' }}><ClockCircleOutlined /> Variables</Text>
      </div>
      {filteredVariables.length > 0 ? (
        <List
          dataSource={filteredVariables}
          style={{ maxHeight: 150, overflowY: 'auto' }}
          renderItem={([name, varData]) => (
            <List.Item
              onClick={() => handleInsertVariable(name)}
              style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f6ffed'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Space>
                <Tag color="green">{`{{${name}}}`}</Tag>
                <Text type="secondary" style={{ fontSize: 10 }}>{typeof varData === 'object' ? varData.value : varData}</Text>
              </Space>
            </List.Item>
          )}
        />
      ) : (
        <div style={{ padding: '8px 12px', color: '#999', fontSize: 11 }}>No variables defined</div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      overlayClassName="formula-popover"
    >
      <div style={{ position: 'relative' }}>
        <Input.TextArea
            ref={inputRef}
            value={localValue}
            onChange={handleChange}
            onClick={handleClick}
            onKeyUp={handleKeyUp}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder="e.g. price * 1.1"
            autoSize={{ minRows: 1, maxRows: 6 }}
            style={{ paddingRight: 30, fontFamily: 'monospace' }}
        />
        <Tooltip title="Click to insert column or variable">
            <InsertRowBelowOutlined 
                style={{ 
                    position: 'absolute', 
                    right: 8, 
                    top: 8, 
                    color: '#bfbfbf', 
                    cursor: 'pointer',
                    zIndex: 1
                }} 
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }} 
            />
        </Tooltip>
      </div>
    </Popover>
  );
};

const MapNodeProperties = ({ config, inputSchema, updateConfig }) => {
  const [form] = Form.useForm();

  // Sync form with config
  useEffect(() => {
    form.setFieldsValue({
      keepUnmapped: config.keepUnmapped !== false, // Default true
      mappings: config.mappings || []
    });
  }, [config, form]);

  const handleValuesChange = (changedValues, allValues) => {
    if (updateConfig) {
      updateConfig({
        config: allValues
      });
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      <div style={{ marginBottom: 16, padding: '12px', background: '#f0f5ff', borderRadius: 4, border: '1px solid #adc6ff' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <QuestionCircleOutlined /> Map Node dùng để tính toán giá trị cho cột. Bạn có thể chọn cột có sẵn để ghi đè hoặc nhập tên cột mới.
        </Text>
      </div>

      <Form.Item
        name="keepUnmapped"
        valuePropName="checked"
        label={
          <Space>
            <span>Keep other columns</span>
            <Tooltip title="Nếu bật: Giữ lại tất cả các cột khác không được nhắc đến trong danh sách dưới. Nếu tắt: Chỉ giữ lại các cột được liệt kê bên dưới.">
              <QuestionCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
          </Space>
        }
      >
        <Switch />
      </Form.Item>

      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Column Assignments:</Typography.Text>

      <Form.List name="mappings">
        {(fields, { add, remove }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                background: '#fafafa',
                padding: 8,
                borderRadius: 4,
                border: '1px solid #f0f0f0',
                flexDirection: 'column'
              }}>
                <div style={{ width: '100%', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'target']}
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 0, width: '50%' }}
                    label={<span style={{ fontSize: 11 }}>Target Column</span>}
                  >
                    <AutoComplete
                      style={{width:'100%'}}
                      placeholder="Select or create new"
                      options={inputSchema?.map(col => ({ value: col.name, label: col.name }))}
                      filterOption={(inputValue, option) =>
                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                    />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />
                </div>
                <Row style={{ width: '100%' }} gutter={10}>
                  <Col flex={1}>
                    <Form.Item
                      {...restField}
                      name={[name, 'source']}
                      rules={[{ required: true, message: 'Required' }]}
                      style={{ marginBottom: 0 }}
                      label={<span style={{ fontSize: 11 }}>SQL Expression</span>}
                    >
                      <FormulaInput
                        options={inputSchema?.map(col => ({ value: col.name, label: col.name }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}
            <Form.Item style={{ marginTop: 8 }}>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Assignment
              </Button>
            </Form.Item>

            <div style={{ padding: '8px', background: '#f9f9f9', borderRadius: 4, fontSize: 11, color: '#666' }}>
              <strong>Examples (SQL Syntax):</strong>
              <ul style={{ paddingLeft: 16, margin: '4px 0 0 0' }}>
                <li>Copy value: <code>old_column</code></li>
                <li>Math: <code>price * 1.1</code>, <code>ABS(score)</code></li>
                <li>Logic: <code>CASE WHEN age &gt; 18 THEN 'Adult' ELSE 'Minor' END</code></li>
                <li>String: <code>'Prefix_' || sku</code> (Concatenation)</li>
                <li>Casting: <code>CAST(price AS INT)</code></li>
              </ul>
            </div>
          </div>
        )}
      </Form.List>
    </Form>
  );
};

export default MapNodeProperties;